import { UserRolePermission } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { ModalLarge } from '@schooly/style';
import { FC, PropsWithChildren, ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import AccessDeniedPage from '../pages/AccessDenied';
import { ModalAccessDenied } from './uikit-components/Modal/ModalAccessDenied';

type PermissionGuardProps = {
  permissions: UserRolePermission | UserRolePermission[];
  /** 'any' = user needs at least one permission (OR), 'all' = user needs all permissions (AND). Defaults to 'any' */
  mode?: 'any' | 'all';
  fallback?: ReactNode;
};

export const PermissionGuard: FC<PropsWithChildren<PermissionGuardProps>> = ({
  children,
  permissions,
  mode = 'any',
  fallback = <AccessDeniedPage />,
}) => {
  const { permissions: userPermissionsFromAuth } = useAuth();
  const userPermissions = userPermissionsFromAuth ?? [];

  const requiredPermissions = Array.isArray(permissions) ? permissions : [permissions];
  const hasAccess =
    mode === 'any'
      ? requiredPermissions.some((p) => userPermissions.includes(p))
      : requiredPermissions.every((p) => userPermissions.includes(p));

  if (!hasAccess) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

type ModalAccessDeniedFallbackProps = {
  redirectTo: string;
};

export const ModalAccessDeniedFallback: FC<ModalAccessDeniedFallbackProps> = ({ redirectTo }) => {
  const navigate = useNavigate();
  const handleClose = useCallback(() => navigate(redirectTo), [navigate, redirectTo]);

  return (
    <ModalLarge open onClose={handleClose}>
      <ModalAccessDenied />
    </ModalLarge>
  );
};
